import { createStore, StoreApi } from 'zustand/vanilla'
import { createJSONStorage, devtools, persist, StateStorage } from 'zustand/middleware'
import { AppStore } from '@/types/store.types'
import { createUserSlice } from '@/stores/user.slice'
import { createSellerSlice } from '@/stores/seller.slice'
import { createAppSlice } from '@/stores/app.slice'
import { createGeneralSlice } from '@/stores/general.slice'
import { logger } from '@/utils/logger'
import { getStaleStateReason } from '@/utils/store.utils'
import { safeLocalStorage } from '@/utils/storage.utils'

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
}

export const STORE_KEY = 'app-state'

export const createOurStore = (
  prefix: string,
  timezone?: string,
): StoreApi<AppStore> => {
  const store = createStore<AppStore>()(
    devtools(
      persist(
        (...a) => ({
          ...createUserSlice(...a),
          ...createSellerSlice(...a),
          ...createAppSlice(...a),
          ...createGeneralSlice(...a),
        }),
        {
          version: 0,
          name: `${prefix}__${STORE_KEY}`,
          // safeLocalStorage never throws: some in-app browsers have no localStorage, and zustand
          // would otherwise throw on every set() or turn persistence off and never hydrate.
          storage: createJSONStorage(() => (prefix === 'test' ? noopStorage : safeLocalStorage)),
          partialize: (state) => {
            // Don't persist the following keys
            const {
              _hasHydrated,
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ready, siteIsVisible, userIsIdle, loadingBlob,
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              calendarState,
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              doActionContainerScroll, errorNotification, // reactiveVars
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              startTimeFromUrlParam,
              ...rest
            } = state

            return rest
          },
          // Clears the entire store if the persisted activeDate is in the past,
          // preventing a stale calendar selection from carrying over to a new session.
          // docs: https://zustand.docs.pmnd.rs/reference/middlewares/persist#parameters
          onRehydrateStorage: (initialState) => (state, error) => {
            // Hydration failed (e.g. the stored value isn't valid JSON). Carry on with the defaults:
            // components wait on `_hasHydrated`, so leaving it false shows a spinner forever.
            if (!state) {
              logger().warn('[onRehydrateStorage] failed, using defaults', error)
              initialState.setHydrated(true)
              return
            }

            state.setHydrated(true)

            const {
              activeDate,
              activeStartTime,
              resetAll,
            } = state

            const { log } = logger()

            const staleReason = getStaleStateReason({
              activeDate,
              activeStartTimeEndTime: activeStartTime.endTime,
              timezone,
            })

            if (staleReason) {
              log(`[onRehydrateStorage] ${staleReason}`)
              resetAll()
              return
            }
          },
        },
      ),
    ),
  )

  return store
}
